import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Customer, Driver, RouteSaveData } from './types';
import { useHttpClient } from '../../../shared/http-client';

interface RouteModalProps {
    customers: Customer[];
    areaId: number | null;
    onClose: () => void;
    onSave: (data: RouteSaveData) => void;
}

export const RouteModal = ({ customers, areaId, onClose, onSave }: RouteModalProps) => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();

    const [routeDate, setRouteDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [withoutDate, setWithoutDate] = useState<boolean>(false);
    const [isCommercial, setIsCommercial] = useState<boolean>(false);
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [routeConfigs, setRouteConfigs] = useState<any[]>([]);
    const [selectedRouteConfigId, setSelectedRouteConfigId] = useState<number | null>(null);
    const [selectedDriver, setSelectedDriver] = useState<number | null>(null);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [selectedVehicle, setSelectedVehicle] = useState<number | null>(null);
    const [loadingVehicles, setLoadingVehicles] = useState<boolean>(false);

    const [workdayStartTime, setWorkdayStartTime] = useState<string>('');
    const [workdayEndTime, setWorkdayEndTime] = useState<string>('');
    const [breakStartTime, setBreakStartTime] = useState<string>('');
    const [breakEndTime, setBreakEndTime] = useState<string>('');
    const [breakDurationHours, setBreakDurationHours] = useState<string>('');
    const [estimatedVisitTimeMinutes, setEstimatedVisitTimeMinutes] = useState<string>('');
    const [maxDistancePerDayKm, setMaxDistancePerDayKm] = useState<string>('');
    const [customersList, setCustomersList] = useState<Array<{ customer: Customer; time: string; duration: string }>>(
        customers.map(customer => ({
            customer,
            time: '',
            duration: '60'
        }))
    );

    const handleTimeChange = (index: number, time: string) => {
        const updated = [...customersList];
        updated[index].time = time;
        setCustomersList(updated);
    };

    const handleDurationChange = (index: number, duration: string) => {
        const updated = [...customersList];
        updated[index].duration = duration;
        setCustomersList(updated);
    };

    const handleRemove = (index: number) => {
        const updated = customersList.filter((_, i) => i !== index);
        setCustomersList(updated);
    };

    const handleSave = () => {
        const driver = drivers.find(d => d.id === selectedDriver) || null;
        const vehicle = vehicles.find(v => v.id === selectedVehicle) || null;
        if (!vehicle) {
            return;
        }

        const selectedDriverObj = drivers.find(d => d.id === selectedDriver);
        const hasCoords = selectedDriverObj?.latitude != null && selectedDriverObj?.longitude != null;

        if (!vehicle.latitude || !vehicle.longitude) {
            if (!hasCoords) {
                toast.error(t('common.label.no-valid-coordinates'));
                return;
            }

        }
        const invalidCustomer = customersList.some((item) => item.customer.id == null);
        if (invalidCustomer) {
            return;
        }

        const toBackendStartTime = (date: string, time: string) => {
            const hhmm = time.trim();
            return `${date}T${hhmm}:00Z`;
        };

        const customersWithDate = customersList.map((item) => ({
            customer: item.customer.id as number,
            ...(item.time && !withoutDate ? { start_time: toBackendStartTime(routeDate, item.time) } : {}),
            duration: (Number.isFinite(Number(item.duration)) ? Number(item.duration) : 0) * 60
        }));
        onSave({
            customers: customersWithDate,
            driver,
            vehicle,
            ...(withoutDate ? {} : { routeDate }),
            areaId,
            default_driver_address: isCommercial,
            workday_start_time: workdayStartTime,
            workday_end_time: workdayEndTime,
            break_start_time: breakStartTime,
            break_end_time: breakEndTime,
            break_duration_hours: Number(breakDurationHours),
            estimated_visit_time_minutes: Number(estimatedVisitTimeMinutes),
            max_distance_per_day_km: Number(maxDistancePerDayKm)
        });
    };

    const handleDriverChange = (driverId: number | null) => {
        setSelectedDriver(driverId);
        setSelectedVehicle(null);
        setVehicles([]);
        if (driverId) {
            setLoadingVehicles(true);
            api(`/fleet/assignments/?driver=${driverId}&is_active=true`, 'GET');
        } else {
            setLoadingVehicles(false);
        }
    };

    useEffect(() => {
        api(`/fleet/drivers/?area=${areaId}`, 'GET');
    }, []);

    useEffect(() => {
        api('/fleet/route-configs/?limit=9999', 'GET');
    }, []);

    useEffect(() => {
        if (state.path?.includes('/fleet/drivers/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                /* const filteredDrivers = areaId
                    ? state.data.results.filter((driver: Driver) => driver.area?.id === areaId)
                    : state.data.results; */
                setDrivers(state.data.results);
            }
        }
        if (state.path?.includes('/fleet/route-configs/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                setRouteConfigs(state.data.results);
            }
        }
        if (state.path?.includes('/fleet/assignments/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                const vehiclesFromAssignments = state.data.results
                    .filter((assignment: any) => assignment.vehicle)
                    .map((assignment: any) => assignment.vehicle);
                setVehicles(vehiclesFromAssignments);
            }
            setLoadingVehicles(false);
        }
    }, [state, areaId]);

    // Auto-select vehicle when only one is available
    useEffect(() => {
        if (vehicles.length === 1 && !selectedVehicle) {
            setSelectedVehicle(vehicles[0].id);
        }
    }, [vehicles]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {t('common.label.crear-ruta')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex gap-4">
                            {!withoutDate ? (
                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">
                                        {t('common.labels.date')}
                                    </label>
                                    <input
                                        type="date"
                                        value={routeDate}
                                        onChange={(e) => setRouteDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
                                    />
                                </div>
                            ) : null}
                            <div className="flex items-end">
                                <div className="flex items-center" style={{ paddingBottom: 13 }}>
                                    <input
                                        id="without_date"
                                        name="without_date"
                                        type="checkbox"
                                        checked={withoutDate}
                                        onChange={(e) => {
                                            setWithoutDate(e.target.checked);
                                        }}
                                        className="h-4 w-4 text-primary focus:ring-indigo-500 border-gray-300"
                                    />
                                    <label htmlFor="without_date" className="ml-2 block text-sm text-gray-900">
                                        {t('common.label.sin-fecha-horario-inicio-servicio')}
                                    </label>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-gray-600 mb-1">
                                    {t('common.label.conductor')}
                                </label>
                                <select
                                    value={selectedDriver || ''}
                                    onChange={(e) => handleDriverChange(e.target.value ? Number(e.target.value) : null)}
                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">{t('common.label.comercial')}</option>
                                    {drivers.map((driver) => (
                                        <option key={driver.id} value={driver.id}>
                                            {driver.full_name} {driver.area ? `- ${driver.area.name}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-gray-600 mb-1">
                                    {t('common.label.vehiculo')}
                                </label>
                                {loadingVehicles ? (
                                    <div className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500 flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>{t('common.loading')}</span>
                                    </div>
                                ) : selectedDriver && vehicles.length === 0 ? (
                                    <div className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500 italic">
                                        {t('common.label.no-vehiculos-asignados')}
                                    </div>
                                ) : vehicles.length === 1 ? (
                                    <div className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-700">
                                        {vehicles[0].plate} - {vehicles[0].brand} {vehicles[0].model?.name || ''}
                                    </div>
                                ) : (
                                    <select
                                        value={selectedVehicle || ''}
                                        onChange={(e) => setSelectedVehicle(e.target.value ? Number(e.target.value) : null)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={!selectedDriver || loadingVehicles}
                                    >
                                        <option value="">{t('common.label.select-vehiculo')}</option>
                                        {vehicles.map((vehicle) => (
                                            <option key={vehicle.id} value={vehicle.id}>
                                                {vehicle.plate} - {vehicle.brand} {vehicle.model?.name || ''}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>


                        </div>
                        <div>
                            {(() => {
                                const selectedDriverObj = drivers.find(d => d.id === selectedDriver);
                                const hasCoords = selectedDriverObj?.latitude != null && selectedDriverObj?.longitude != null;
                                return hasCoords ? (
                                    <div className="flex items-end pt-4">
                                        <div className="flex items-center" style={{ paddingBottom: 6 }}>
                                            <input
                                                id="default_driver_address"
                                                name="default_driver_address"
                                                type="checkbox"
                                                checked={isCommercial}
                                                onChange={(e) => {
                                                    setIsCommercial(e.target.checked);
                                                }}
                                                className="h-4 w-4 text-primary focus:ring-indigo-500 border-gray-300"
                                            />
                                            <label htmlFor="default_driver_address" className="ml-2 block text-sm text-gray-900">
                                                {t('common.label.desde-punto')}
                                            </label>
                                        </div>

                                    </div>
                                ) : null;
                            })()}
                            {(() => {
                                const selectedDriverObj = drivers.find(d => d.id === selectedDriver);
                                const hasCoords = selectedDriverObj?.latitude != null && selectedDriverObj?.longitude != null;
                                return hasCoords ? (
                                    <p className="text-xs text-gray-700">
                                        {t('common.label.nota')}
                                    </p>
                                ) : null;
                            })()}
                        </div>

                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex flex-col mb-4">
                                <label className="text-xs text-gray-600 mb-1">{t('common.label.configuracion-rutas')}</label>
                                <select
                                    value={selectedRouteConfigId ?? ''}
                                    onChange={(e) => {
                                        const nextId = e.target.value ? Number(e.target.value) : null;
                                        setSelectedRouteConfigId(nextId);

                                        const selected = routeConfigs.find((c) => c.id === nextId);
                                        if (!selected) {
                                            return;
                                        }

                                        const toHHmm = (v: any) => {
                                            if (!v) return '';
                                            const s = String(v);
                                            return s.length >= 5 ? s.slice(0, 5) : s;
                                        };

                                        setWorkdayStartTime(toHHmm(selected.workday_start_time));
                                        setWorkdayEndTime(toHHmm(selected.workday_end_time));
                                        setBreakStartTime(toHHmm(selected.break_start_time));
                                        setBreakEndTime(toHHmm(selected.break_end_time));

                                        if (selected.break_duration_hours !== undefined && selected.break_duration_hours !== null) {
                                            setBreakDurationHours(String(selected.break_duration_hours));
                                        }
                                        if (selected.estimated_visit_time_minutes !== undefined && selected.estimated_visit_time_minutes !== null) {
                                            setEstimatedVisitTimeMinutes(String(selected.estimated_visit_time_minutes));
                                        }
                                        if (selected.max_distance_per_day_km !== undefined && selected.max_distance_per_day_km !== null) {
                                            setMaxDistancePerDayKm(String(selected.max_distance_per_day_km));
                                        }
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">{t('common.label.select')}</option>
                                    {routeConfigs.map((cfg) => (
                                        <option key={cfg.id} value={cfg.id}>
                                            {cfg.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">{t('workday_start_time')}</label>
                                    <input
                                        type="time"
                                        value={workdayStartTime}
                                        onChange={(e) => setWorkdayStartTime(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">{t('workday_end_time')}</label>
                                    <input
                                        type="time"
                                        value={workdayEndTime}
                                        onChange={(e) => setWorkdayEndTime(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">{t('break_start_time')}</label>
                                    <input
                                        type="time"
                                        value={breakStartTime}
                                        onChange={(e) => setBreakStartTime(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">{t('break_end_time')}</label>
                                    <input
                                        type="time"
                                        value={breakEndTime}
                                        onChange={(e) => setBreakEndTime(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">{t('break_duration_hours')}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={breakDurationHours}
                                        onChange={(e) => setBreakDurationHours(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-xs text-gray-600 mb-1">{t('estimated_visit_time_minutes')}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={estimatedVisitTimeMinutes}
                                        onChange={(e) => setEstimatedVisitTimeMinutes(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                </div>

                                <div className="flex flex-col md:col-span-2">
                                    <label className="text-xs text-gray-600 mb-1">{t('max_distance_per_day_km')}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={maxDistancePerDayKm}
                                        onChange={(e) => setMaxDistancePerDayKm(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-4">
                        {customersList.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <p>{t('common.label.no-customers')}</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {customersList.map((item, index) => (
                                    <div
                                        key={item.customer.id ?? item.customer.name}
                                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-800 truncate">
                                                {item.customer.name}
                                            </h3>
                                            {item.customer.address && (
                                                <p className="text-sm text-gray-600 truncate w-[350px]">
                                                    {item.customer.address}
                                                </p>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3">


                                            <div className="flex flex-col w-[150px]">
                                                <label className="text-xs text-gray-600 mb-1">
                                                    {t('common.form-label.hora')}
                                                </label>
                                                <input
                                                    type="time"
                                                    value={item.time}
                                                    onChange={(e) => handleTimeChange(index, e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className="text-xs text-gray-600 mb-1">
                                                    {t('common.label.duracion-minutos')}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={item.duration}
                                                    onChange={(e) => handleDurationChange(index, e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-24"
                                                    placeholder="0"
                                                />
                                            </div>

                                            <button
                                                onClick={() => handleRemove(index)}
                                                className="mt-5 p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                title={t('common.button.delete')}
                                            >
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-[13px] text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
                    >
                        {t('common.button.cancel')}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={customersList.length === 0 || !selectedDriver || !selectedVehicle || !selectedRouteConfigId}
                        className="px-4 py-2 text-[13px] bg-primary hover:bg-primary text-white rounded-md font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {t('common.button.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};
